<template>
  <div>
    <!-- Package Type Selection -->
    <div v-if="!showPackageList" class="payment-method-list">
      <div class="payment-method-item" :class="{ 'disabled': loading }" @click="!loading && selectPackageType(1)">
        <i v-if="loading" class="fas fa-spinner fa-spin fa-2x"></i>
        <i v-else class="fas fa-cube fa-2x"></i>
        <span>{{ loading ? 'Saving...' : 'Normal' }}</span>
      </div>
      <div class="payment-method-item" :class="{ 'disabled': loading }" @click="!loading && selectPackageType(2)">
        <i class="fa fa-cubes fa-2x"></i>
        <span>Package</span>
      </div>
    </div>

    <!-- Promotion Package List (shown when Package is selected) -->
    <div v-if="showPackageList">
      <!-- <label class="selection-label">Select Package</label> -->

      <div v-if="promotionPackages && promotionPackages.length > 0" class="package-list">
        <div v-for="(pkg, index) in promotionPackages" :key="pkg.package_id || index" class="pkg-card"
          :class="{ 'pkg-card--selected': selectedPackage && selectedPackage.package_id === pkg.package_id }"
          @click="() => selectedPackage = pkg">

          <!-- Card Header -->
          <div class="pkg-card__header">
            <div class="pkg-card__title-block">
              <div class="pkg-card__name">{{ pkg.package_name }}</div>
              <div class="pkg-card__ref">Package ID: {{ pkg.package_id }}</div>
            </div>
            <div class="pkg-card__amount-block">
              <div class="pkg-card__amount-label">TOTAL AMOUNT</div>
              <div class="pkg-card__amount">{{ formatAmount(pkgTotal(pkg)) }}</div>
            </div>
          </div>

          <!-- Divider -->
          <div class="pkg-card__divider"></div>

          <!-- Details Toggle Row -->
          <div v-if="pkg.details && pkg.details.length > 0" class="pkg-card__toggle"
            @click.stop="toggleDetails(pkg.package_id)">
            <span class="pkg-card__toggle-label">{{ expandedPackages[pkg.package_id] ? 'Hide' : 'Show' }} items ({{
              pkg.details.length }})</span>
            <i :class="expandedPackages[pkg.package_id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
              class="pkg-card__toggle-icon"></i>
          </div>

          <!-- Details Table -->
          <div v-if="pkg.details && pkg.details.length > 0 && expandedPackages[pkg.package_id]" class="pkg-card__body">
            <table class="pkg-table">
              <colgroup>
                <col />
                <col />
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th class="pkg-table__th">Product</th>
                  <th class="pkg-table__th text-center">QTY</th>
                  <th class="pkg-table__th text-right">Unit Price</th>
                  <th class="pkg-table__th text-center">Discount</th>
                  <th class="pkg-table__th text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(detail, dIndex) in pkg.details" :key="detail.package_detail_id || dIndex"
                  class="pkg-table__row">
                  <td class="pkg-table__td">
                    <div class="pkg-table__product-name">{{ detail.product_name_en }}</div>
                    <div class="pkg-table__barcode">{{ detail.barcode }}</div>
                  </td>
                  <td class="pkg-table__td text-center">{{ detail.quantity }} {{ detail.uom_name_en }}</td>
                  <td class="pkg-table__td text-right">{{ formatAmount(detail.input_unit_price) }}</td>
                  <td class="pkg-table__td text-center">
                    <span v-if="detail.discount_rate" class="pkg-table__discount-badge">
                      {{ detail.discount_rate }}{{ detail.discount_type }}
                    </span>
                    <span v-else class="pkg-table__no-discount">—</span>
                  </td>
                  <td class="pkg-table__td text-right pkg-table__total">{{ formatAmount(detail.input_grand_total) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-else class="package-empty">
        <i class="fas fa-box-open fa-2x"></i>
        <span>No packages available</span>
      </div>
      <div class="pkg-footer">
        <ts-button type="submit" class="btn-lg" @click.prevent="() => showPackageList = false" :waiting="waiting">
          <i class="fas fa-arrow-left"></i> Back
        </ts-button>
        <ts-button type="submit" class="btn-lg" color="primary" @click.prevent="confirmPackage" :waiting="waiting">{{
          $t('ok') }}</ts-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'PaymentMethodSelection',
  props: {
    table: {
      type: Object,
      default: {}
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedTable: null,
      showPackageList: false,
      waiting: false,
      selectedPackage: null,
      expandedPackages: {}
    }
  },
  computed: {
    ...mapGetters('restaurantSale/customerOrder', ['getPromotionPackage']),
    promotionPackages() {
      return this.getPromotionPackage()
    },
  },
  watch: {
    table: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.selectedTable = { ...newVal }
        }
      }
    }
  },
  methods: {
    pkgTotal(pkg) {
      if (pkg.grand_total && pkg.grand_total > 0) return pkg.grand_total;
      if (pkg.details && pkg.details.length > 0) {
        return pkg.details.reduce((sum, d) => sum + (parseFloat(d.input_grand_total) || 0), 0);
      }
      return pkg.sub_total || 0;
    },
    formatAmount(val) {
      const num = parseFloat(val) || 0;
      return num % 1 === 0 ? num.toFixed(2) : num.toFixed(2);
    },
    toggleDetails(packageId) {
      this.$set(this.expandedPackages, packageId, !this.expandedPackages[packageId]);
    },
    selectPackageType(checkInType) {
      this.selectedTable = { ...this.selectedTable, checking_type: checkInType };
      if (checkInType === 1) {
        this.$emit('confirm', this.selectedTable);
      } else if (checkInType === 2) {
        this.selectedPackage = null;
        this.showPackageList = true;
      }
    },
    handlePackageSelected() {
      if (!this.selectedPackage) return;
      this.selectedTable = {
        ...this.selectedTable,
        checking_type: 2,
        promotion_id: this.selectedPackage.promotion_id,
        package_id: this.selectedPackage.package_id,
        package_name: this.selectedPackage.package_name
      };

      this.$emit('confirm', this.selectedTable);
    },
    confirmPackage() {
      this.selectedTable.invoice_detail = this.selectedPackage.details.map(el => ({
        amount: el.sub_total,
        discount_amount: el.discount_amount,
        discount_rate: el.discount_rate,
        discount_type: el.discount_type,
        grand_total: el.grand_total,
        is_allow_adjust_free_quantity: false,
        is_pre_charge: true,
        max_person: null,
        product_id: el.product_id,
        promo_free_item_cond_id: null,
        promo_type_id: null,
        quantity: el.quantity,
        remarks: null,
        service_length: el.service_length,
        total_after_discount: el.grand_total,
        unit_price: el.unit_price,
        uom_id: el.uom_id,
        vat_amount: el.vat_amount,
        vat_rate: el.vat_rate,
      }));

      this.$emit('confirm', this.selectedTable);
    },
  }
}
</script>

<style scoped>
/* Payment Method Modal Styles */
.payment-method-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  padding: 10px;
}

.payment-method-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 25px 15px;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.payment-method-item:hover {
  border-color: #1890ff;
  background-color: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.payment-method-item i {
  color: #666;
  margin-bottom: 10px;
  transition: color 0.3s ease;
}

.payment-method-item:hover i {
  color: #1890ff;
}

.payment-method-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.payment-method-item span {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  text-align: center;
}

/* Package List */
.package-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  max-height: none;
}

/* Package Card */
.pkg-card {
  background: #fff;
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  overflow: hidden;
}

.pkg-card:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 10px rgba(24, 144, 255, 0.1);
}

.pkg-card--selected {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.15);
}

/* Toggle Row */
.pkg-card__toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 14px;
  background: #f9f9f9;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
}

.pkg-card__toggle:hover {
  background: #f0f7ff;
}

.pkg-card__toggle-label {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.pkg-card__toggle-icon {
  font-size: 10px;
  color: #aaa;
}

/* Card Header */
.pkg-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px 8px;
  gap: 10px;
}

.pkg-card__title-block {
  min-width: 0;
}

.pkg-card__name {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pkg-card__ref {
  font-size: 11px;
  color: #bbb;
}

.pkg-card__amount-block {
  text-align: right;
  flex-shrink: 0;
}

.pkg-card__amount-label {
  font-size: 9px;
  font-weight: 700;
  color: #bbb;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  margin-bottom: 0;
}

.pkg-card__amount {
  font-size: 20px;
  font-weight: 800;
  color: #1890ff;
  line-height: 1.1;
  letter-spacing: -0.5px;
}

/* Divider */
.pkg-card__divider {
  border: none;
  border-top: 1px dashed #ebebeb;
  margin: 0 14px;
}

/* Card Body / Table */
.pkg-card__body {
  padding: 0 14px 6px;
}

.pkg-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  table-layout: fixed;
}

.pkg-table colgroup col:nth-child(1) {
  width: 40%;
}

.pkg-table colgroup col:nth-child(2) {
  width: 14%;
}

.pkg-table colgroup col:nth-child(3) {
  width: 16%;
}

.pkg-table colgroup col:nth-child(4) {
  width: 14%;
}

.pkg-table colgroup col:nth-child(5) {
  width: 16%;
}

.pkg-table__th {
  padding: 7px 4px 6px;
  font-size: 10px;
  font-weight: 700;
  color: #c0c0c0;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
}

.pkg-table__th:first-child {
  padding-left: 0;
}

.pkg-table__th:last-child {
  padding-right: 0;
}

.pkg-table__row {
  border-bottom: 1px solid #f7f7f7;
}

.pkg-table__row:last-child {
  border-bottom: none;
}

.pkg-table__td {
  padding: 7px 4px;
  color: #444;
  vertical-align: middle;
}

.pkg-table__td:first-child {
  padding-left: 0;
}

.pkg-table__td:last-child {
  padding-right: 0;
}

.pkg-table__product-name {
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pkg-table__barcode {
  font-size: 10px;
  color: #bbb;
}

.pkg-table__discount-badge {
  display: inline-block;
  background: #f6ffed;
  color: #389e0d;
  border: 1px solid #b7eb8f;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
}

.pkg-table__no-discount {
  color: #d0d0d0;
}

.pkg-table__total {
  font-weight: 700;
  color: #1a1a1a;
}


/* Helpers */
.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.package-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 0;
  color: #999;
  font-size: 14px;
}

/* Bank Selection Styles */
.bank-selection-section,
.account-selection-section {
  margin-bottom: 20px;
}

.pkg-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
}

.pkg-footer .btn-lg {
  padding: 5px 28px;
  font-size: 15px;
  height: auto;
}

.selection-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
}

.bank-logo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.bank-logo-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.bank-logo-item:hover {
  border-color: #1890ff;
  background-color: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.bank-logo-item.selected {
  border-color: #1890ff;
  background-color: #e6f7ff;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
}

.bank-logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 4px;
  margin-bottom: 8px;
}

.bank-name {
  font-size: 12px;
  font-weight: 500;
  color: #333;
  text-align: center;
  word-break: break-word;
}

.account-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.account-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.account-item:hover {
  border-color: #1890ff;
  background-color: #f0f7ff;
}

.account-item.selected {
  border-color: #1890ff;
  background-color: #e6f7ff;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
}

.account-info {
  flex: 1;
}

.account-bank {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.account-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
  text-transform: uppercase;
}

.account-number {
  font-size: 13px;
  color: #666;
}

.account-item i {
  color: #1890ff;
  font-size: 20px;
}

.modal-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e8e8e8;
}

.btn-back,
.btn-confirm {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-back {
  background-color: #f5f5f5;
  color: #666;
}

.btn-back:hover {
  background-color: #e8e8e8;
  color: #333;
}

.btn-confirm {
  background-color: #1890ff;
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background-color: #40a9ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
}

.btn-confirm:disabled {
  background-color: #d9d9d9;
  color: #999;
  cursor: not-allowed;
}
</style>
